import { Error, Paging } from "api/baseApiResponseModel/BaseApiResponseModel";
import { CarrierListRequestModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListRequestModel";
import { NationListRequestModel } from "api/merchantRepository/filterFeature/model/nationList/NationListRequestModel";
import { NationListResponseModel } from "api/merchantRepository/filterFeature/model/nationList/NationListResponseModel";
import { PackageListRequestModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListRequestModel";
import { PackageListResponseModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListResponseModel";
import { TravelSimRegistrationRequestModel } from "api/merchantRepository/travelSimRegistration/model/TravelSimRegistrationRequestModel";
import { TravelSimRegistrationResponseModel } from "api/merchantRepository/travelSimRegistration/model/TravelSimRegistrationResponseModel";
import { CarrierResponseModel } from "api/repositories/carrier/model/CarrierResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { BaseViewModel } from "components/base/BaseViewModel";
import dayjs from "dayjs";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";

class TravelSimRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<TravelSimRegistrationRequestModel>(
    new TravelSimRegistrationRequestModel()
  );
  public fetchCarrier = new BehaviorSubject<CarrierListRequestModel>(
    new CarrierListRequestModel()
  );
  public exportExcel = new BehaviorSubject<TravelSimRegistrationRequestModel>(
    new TravelSimRegistrationRequestModel()
  );
  public fetchNation = new BehaviorSubject<NationListRequestModel>(
    new NationListRequestModel()
  );
  public fetchPackage = new BehaviorSubject<PackageListRequestModel>(
    new PackageListRequestModel()
  );
  public fetchDetail = new BehaviorSubject<TravelSimRegistrationRequestModel>(
    new TravelSimRegistrationRequestModel()
  );
  public loadStores = new BehaviorSubject<{ userType: number }>({
    userType: 0,
  });
  public loadStaff = new BehaviorSubject<{
    selectedStore: Array<string>;
    userType: number;
  }>({ selectedStore: [], userType: 0 });

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.fetchNation.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.fetchPackage.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
  }
}

class TravelSimRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<TravelSimRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<CarrierResponseModel>>([]);
  public savedDetail = new BehaviorSubject<TravelSimRegistrationResponseModel>(
    {}
  );
  public savedPackage = new BehaviorSubject<Array<PackageListResponseModel>>(
    []
  );
  public savedNation = new BehaviorSubject<Array<NationListResponseModel>>([]);
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public storeOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  public staffOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedNation.complete();
    this.savedPackage.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.storeOptions.complete();
    this.staffOptions.complete();
  }
}

@injectable()
export class TravelSimRegistrationViewModel extends BaseViewModel<
  TravelSimRegistrationInput,
  TravelSimRegistrationOuput
> {
  private repo = ServiceRepo.getTravelSimRegistrationRepo();
  private filterRepo = ServiceRepo.getFilterFeatureRepo();

  constructor() {
    super(new TravelSimRegistrationInput(), new TravelSimRegistrationOuput());
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.getInboundList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data ?? []);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchNation, async (params) => {
      try {
        const res = await this.filterRepo.getNationList(params);
        if (res?.data) {
          this.output.savedNation.next(res?.data ?? []);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (params) => {
      const cache = productCache?.getCache("inboundPackage");
      if (cache?.length) {
        this.output.savedPackage.next(cache);
      } else {
        try {
          const res = await this.filterRepo.getPackageList(params);
          if (res?.data) {
            this.output.savedPackage.next(res?.data);
            productCache?.saveCache(res?.data, "inboundPackage");
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(error);
        }
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.filterRepo.getCarrierList(params);
        if (res?.data) {
          let finalData = res?.data?.filter(
            (child) =>
              child?.code === "viettel" ||
              child?.code === "mobifone" ||
              child?.code === "vinaphone"
          );
          this.output.savedCarrier.next(finalData);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.repo.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.inboundExportReport(params);
        if (res?.data) {
          const url = window.URL.createObjectURL(res?.data as Blob);
          const link = document.createElement("a");
          const timestamp = dayjs().format("YYYYMMDD_HHmmss");
          link.href = url;
          link.setAttribute(
            "download",
            `HELLO_VIETNAM_SIM_REPORT_${timestamp}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async ({ userType }) => {
      try {
        const cache = productCache.getCache("storeList");
        if (userType === 3) {
          if (!cache?.length) {
            const res = await defaultStoreRepository.getStoreList({
              page: 0,
              limit: 500,
              stauts: 0,
            } as any);
            const items = res?.data?.data?.items || [];
            const opts = [
              { label: strings.GlobalLabels.All, value: "" },
              ...items.map((s) => ({
                label: s?.name || s?.code,
                value: s?.userId,
                code: s?.code,
              })),
            ];
            productCache.saveCache(
              opts as unknown as Record<string, unknown>,
              "storeList"
            );
            this.output.storeOptions.next(opts);
          } else {
            this.output.storeOptions.next(cache);
          }
        }
      } catch (e) {
        this.output.storeOptions.next([
          { label: strings.GlobalLabels.All, value: "" },
        ]);
      }
    });

    this.skipFirstInitial(
      this.input.loadStaff,
      async ({ selectedStore, userType }) => {
        let isAgency = userType === 3 && selectedStore?.length;
        let isStore = userType === 4;
        if (!selectedStore?.length && userType === 3) {
          this.output.staffOptions.next([
            {
              label: strings.GlobalMessage.InvalidStore,
              value: "",
            },
          ]);
        } else {
          try {
            if (isAgency || isStore) {
              const res = await defaultUserManagementRepository.getUserListV2({
                page: 0,
                limit: 500,
                status: 0,
                ...(selectedStore ? { store: selectedStore } : {}),
              } as any);
              const items = res?.data?.items || [];
              const opts = [
                { label: strings.GlobalLabels.All, value: "" },
                ...items.map((u: any) => ({
                  label: u?.fullName || u?.userName || u?.phone,
                  value: u?.id,
                  type: u?.type,
                })),
              ];
              this.output.staffOptions.next(opts);
            }
          } catch (e) {
            this.output.staffOptions.next([
              { label: strings.GlobalLabels.All, value: "" },
            ]);
          }
        }
      }
    );
  }
}
